// frontend/src/marketing/pricingMath.ts
//
// Pricing revamp (2026-07-25): single source of pure math for the marketing
// pages (PricingPage's calculator + comparison table, LandingPage's teaser +
// example workspace). Mirrors the real billing engine
// (api/modules/platform-billing/billing-engine.ts + seat-metering.ts) and the
// locked launch book (api/migrations/058_pricing_revamp.sql):
//
//   Core            $9/mo   flat, always on (dashboard, records, roles &
//                           access, security, support — plus email log,
//                           expenses, and payments included free)
//   Invoicing       $19/mo  flat
//   Projects        $15/mo  flat
//   E-Sign          $10/mo  flat
//   Time & Attendance $2/mo per CLOCK-IN employee
//   Monitoring       $7/mo per MONITORED employee
//   Payroll          $15/mo including 10 employees, then $1.50 each
//
// Seat vocabulary is exact: "clock-in employee" / "monitored employee" /
// plain "employee" for Payroll (2026-07-29: the basis moved from "who got
// actually paid" to active headcount, so the distinct "paid employee" noun
// is retired). An employee is billed clock-in OR monitored — never both (if
// Monitoring is on, a monitored employee's $7 replaces their $2 clock-in
// charge rather than adding to it). Payroll is independent and always stacks
// on top when enabled, billed as a base fee covering an allowance of
// employees plus a per-employee rate for anyone beyond that allowance.
//
// Billing is PREPAID (2026-07-29): invoices are issued at the START of the
// period they cover, priced off the workspace as it stands that day. Module
// add/remove mid-cycle prorates the remaining days; seat/employee counts are
// NOT prorated — a hire mid-month rides free until the next charge, and
// (the honest other half) a removal mid-month isn't refunded for the days
// already paid for.
//
// These are FALLBACK numbers only — live values come from
// GET /api/public/site/prices (module_prices, super-admin editable) via
// useLivePrices(). Keep in sync with migration 058 (+ the payroll-band
// migration) and ManagePlanDialog's FALLBACK_PRICES
// (frontend/src/modules/platform-billing/ManagePlanDialog.tsx).

import type { PriceMap } from './api';

export type PricedModuleKey =
  | 'core' | 'invoicing' | 'projects' | 'esign' | 'hr' | 'monitoring' | 'payroll';

export const FALLBACK_PRICES: Record<PricedModuleKey, number> = {
  core: 9,
  invoicing: 19,
  projects: 15,
  esign: 10,
  hr: 2,          // seat_amount_per_month — per clock-in employee
  monitoring: 7,  // seat_amount_per_month — per monitored employee
  payroll: 1.5,   // seat_amount_per_month — per employee BEYOND the included band
};

/** Flat base carried by a metered module (0 for hr/monitoring — every seat is
 *  billed from the first one; Payroll's base bundles an included allowance). */
export const FALLBACK_SEAT_BASES: Record<'hr' | 'monitoring' | 'payroll', number> = {
  hr: 0,
  monitoring: 0,
  payroll: 15,
};

/** Seats bundled into the base price before the per-seat rate kicks in. */
export const FALLBACK_INCLUDED_SEATS: Record<'hr' | 'monitoring' | 'payroll', number> = {
  hr: 0,
  monitoring: 0,
  payroll: 10,
};

/** module_key → the seat vocabulary noun used across the marketing copy.
 *  Mirrors billing-engine.ts's SEAT_NOUNS + ManagePlanDialog's SEAT_LABEL. */
export const SEAT_LABEL: Record<'hr' | 'monitoring' | 'payroll', string> = {
  hr: 'clock-in employee',
  monitoring: 'monitored employee',
  payroll: 'employee',
};

/** Base (flat) price for a module, live-overlaid with fallback. Payroll's
 *  "base" is its included-allowance base fee, not a per-seat number. */
export function baseFor(live: PriceMap | null, key: PricedModuleKey): number {
  const lp = live?.[key];
  if (lp) return lp.amount_per_month;
  if (key === 'hr' || key === 'monitoring' || key === 'payroll') return FALLBACK_SEAT_BASES[key];
  return FALLBACK_PRICES[key];
}

/** Per-seat price for a metered module, live-overlaid with fallback. */
export function seatFor(live: PriceMap | null, key: 'hr' | 'monitoring' | 'payroll'): number {
  const lp = live?.[key];
  return lp ? lp.seat_amount_per_month : FALLBACK_PRICES[key];
}

/** Seats bundled into a metered module's base price, live-overlaid with fallback. */
export function includedSeatsFor(live: PriceMap | null, key: 'hr' | 'monitoring' | 'payroll'): number {
  const lp = live?.[key];
  return lp?.included_seats ?? FALLBACK_INCLUDED_SEATS[key];
}

/**
 * Formats Payroll's price display for marketing copy, e.g.
 * "$15/mo — includes 10 employees, then $1.50 each". Whole-dollar amounts
 * render without a decimal; fractional ones keep two places.
 */
export function formatPayrollPriceLabel(basePrice: number, seatPrice: number, included: number): string {
  const fmt = (n: number) => (Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`);
  return `${fmt(basePrice)}/mo — includes ${included} employees, then ${fmt(seatPrice)} each`;
}

export interface MonthlyTotalInputs {
  corePrice: number;
  invoicingPrice: number;
  projectsPrice: number;
  esignPrice: number;
  hrSeatPrice: number;
  monitoringSeatPrice: number;
  /** Payroll's flat base fee — covers the first `payrollIncluded` employees. */
  payrollBasePrice: number;
  /** Payroll's per-employee rate for anyone beyond `payrollIncluded`. */
  payrollSeatPrice: number;
  /** Employees bundled into `payrollBasePrice` before the per-employee rate applies. */
  payrollIncluded: number;
  invoicingOn: boolean;
  projectsOn: boolean;
  esignOn: boolean;
  hrOn: boolean;
  monitoringOn: boolean;
  payrollOn: boolean;
  /** Total active employees in the workspace. */
  employees: number;
  /** Of those, how many are opted into Monitoring (must be <= employees). */
  monitored: number;
}

export interface MonthlyTotalBreakdown {
  core: number;
  flats: number;
  /** Clock-in employees billed = employees - monitored (when Monitoring is
   *  also on — an employee is billed clock-in OR monitored, never both). */
  hrBillableCount: number;
  hrCost: number;
  monitoredCount: number;
  monitoringCost: number;
  /** Employees bundled into Payroll's base fee (echoes the input, 0 when off). */
  payrollIncluded: number;
  /** Employees billed at Payroll's per-employee rate, beyond the included allowance. */
  payrollBillableCount: number;
  payrollCost: number;
  total: number;
}

/**
 * Pure, deterministic monthly-total math — mirrors the real billing engine.
 * Core is unconditional. Flats (Invoicing/Projects/E-Sign) sum for whichever
 * are toggled on. Time & Attendance bills clock-in employees = total minus
 * whichever of those are already billed as monitored (only when Monitoring
 * is also on). Monitoring bills the monitored count directly. Payroll bills
 * its flat base (covering an included allowance of employees) plus its
 * per-employee rate for anyone beyond that allowance.
 */
export function computeMonthlyTotal(i: MonthlyTotalInputs): MonthlyTotalBreakdown {
  const employees = Math.max(0, Math.round(i.employees) || 0);
  const monitored = Math.min(employees, Math.max(0, Math.round(i.monitored) || 0));

  const flats =
    (i.invoicingOn ? i.invoicingPrice : 0) +
    (i.projectsOn ? i.projectsPrice : 0) +
    (i.esignOn ? i.esignPrice : 0);

  const hrBillableCount = i.hrOn ? Math.max(0, employees - (i.monitoringOn ? monitored : 0)) : 0;
  const hrCost = hrBillableCount * i.hrSeatPrice;

  const monitoredCount = i.monitoringOn ? monitored : 0;
  const monitoringCost = monitoredCount * i.monitoringSeatPrice;

  const payrollBillableCount = i.payrollOn ? Math.max(0, employees - i.payrollIncluded) : 0;
  const payrollCost = i.payrollOn
    ? Math.round((i.payrollBasePrice + payrollBillableCount * i.payrollSeatPrice) * 100) / 100
    : 0;

  const total = i.corePrice + flats + hrCost + monitoringCost + payrollCost;

  return {
    core: i.corePrice,
    flats,
    hrBillableCount,
    hrCost,
    monitoredCount,
    monitoringCost,
    payrollIncluded: i.payrollOn ? i.payrollIncluded : 0,
    payrollBillableCount,
    payrollCost,
    total,
  };
}
